import { useEffect, useRef, useState } from 'react';
import { tickToSeconds, markQpm } from '@simplenotation/core';
import { useStore, setPlaybackPrefs, setView, setState, openModal, type ViewPrefs } from '../state/store';
import * as player from '../playback/player';
import { Icon, Toggle, Segmented } from './common';
import { formatShortcut } from '../commands/registry';
import { IS_MAC } from '../input/keymap';
import { commandTip } from './CommandButton';
import { tip } from './Tooltip';
import { zoomStep, resetZoom, ZOOM_MIN, ZOOM_MAX } from './zoom';

function fmt(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function Transport() {
  const playing = useStore((s) => s.playback.playing);
  const tick = useStore((s) => s.playback.tick);
  const loop = useStore((s) => s.playback.loop);
  const timeline = useStore((s) => s.timeline);
  const title = useStore((s) => s.ed.score.meta.title);
  const tempo = useStore((s) => s.ed.score.tempos[0]);
  const prefs = useStore((s) => s.playbackPrefs);
  const view = useStore((s) => s.view);
  const metOpen = useStore((s) => s.metronomeOpen);
  const settingsOpen = useStore((s) => s.settingsOpen);
  const speed = prefs.speed;
  const total = tickToSeconds(timeline, timeline.totalTicks, speed);
  const now = tickToSeconds(timeline, Math.min(tick, timeline.totalTicks), speed);
  const written = tempo ? tempo.bpm : 120;
  const bpm = Math.round(written * speed);
  const pct = Math.round(speed * 100);
  const progress = timeline.totalTicks ? Math.min(1, tick / timeline.totalTicks) : 0;
  void markQpm;
  return (
    <div className="transport" data-tour="transport">
      <div
        className="progress"
        role="slider"
        aria-label="Playback position"
        aria-valuemin={0}
        aria-valuemax={Math.round(total)}
        aria-valuenow={Math.round(now)}
        tabIndex={0}
        onMouseDown={(e) => {
          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
          player.seek(((e.clientX - r.left) / r.width) * timeline.totalTicks);
        }}
      >
        <div className="progress-fill" style={{ width: `${progress * 100}%` }} />
      </div>
      <div className="transport-row">
        <button className="play" onClick={() => void player.togglePlay()} aria-label={playing ? 'Pause' : 'Play'} {...tip(playing ? 'Pause' : 'Play', { keys: formatShortcut('Space'), side: 'top' })} data-testid="play">
          <Icon icon={playing ? { svg: 'M7 5h3v14H7zM14 5h3v14h-3z', fill: true } : { svg: 'M8 5l11 7-11 7z', fill: true }} size={20} />
        </button>
        <div className="now-playing">
          <span className="np-title">{title || 'Untitled'}</span>
          <span className="np-time">
            {fmt(now)} / {fmt(total)}
          </span>
        </div>
        <div className="bpm" {...tip('Playback speed', { hint: speed === 1 ? `Plays at the written tempo, ♩ = ${written}. Slower or faster changes playback only, not the score.` : `${pct}% of the written tempo (♩ = ${written}). The score itself is unchanged.`, side: 'top' })}>
          <button aria-label="Slower" onClick={() => player.nudgeSpeed(-0.05)}>
            −
          </button>
          <span className="bpm-value" data-testid="bpm">
            {bpm} BPM
          </span>
          <button aria-label="Faster" onClick={() => player.nudgeSpeed(0.05)}>
            +
          </button>
          <input type="range" min={25} max={200} step={5} value={pct} onChange={(e) => player.setSpeed(Number(e.target.value) / 100)} aria-label="Playback speed (%)" className="speed-range" />
          <span className="speed-pct">{pct}%</span>
          {speed !== 1 && (
            <button className="speed-reset" onClick={() => player.setSpeed(1)} aria-label={`Reset to the written tempo, ${written} BPM`}>
              ↺ {written}
            </button>
          )}
        </div>
        <button className={`tbtn${loop ? ' on' : ''}`} onClick={() => player.toggleLoop()} {...commandTip('play.loop', { hint: 'Plays the selected bars over and over, for practice.', side: 'top' })} aria-pressed={!!loop}>
          <Icon icon={{ svg: 'M17 2l4 4-4 4M3 11V9a3 3 0 0 1 3-3h15M7 22l-4-4 4-4M21 13v2a3 3 0 0 1-3 3H3' }} size={18} /> <span className="label-text">Loop</span>
        </button>
        <button className={`tbtn${view.showFretboard ? ' on' : ''}`} onClick={() => setView({ showFretboard: !view.showFretboard })} aria-pressed={view.showFretboard} {...commandTip('view.fretboard', { hint: 'Shows the notes under the cursor and during playback on a guitar neck.', side: 'top' })}>
          <Icon icon={{ svg: 'M3 8h18M3 12h18M3 16h18M7 6v12M12 6v12M17 6v12' }} size={18} /> <span className="label-text">Fretboard</span>
        </button>
        <span className="spacer" />
        <div className="popwrap">
          <button className={`tbtn icon-only${prefs.metronome.enabled ? ' on' : ''}`} onClick={() => setState((s) => ({ metronomeOpen: !s.metronomeOpen, settingsOpen: false }))} aria-label="Metronome and count-in" {...tip('Metronome & count-in', { hint: 'A click track while playing, and bars counted in before it starts.', side: 'top' })} aria-expanded={metOpen}>
            <Icon icon={{ svg: 'M8 21h8l-3-16h-2zM12 14l6-8' }} size={18} />
          </button>
          {metOpen && <MetronomePopover />}
        </div>
        <button className={`tbtn icon-only${view.showPiano ? ' on' : ''}`} onClick={() => setView({ showPiano: !view.showPiano })} aria-label="Piano keyboard" {...commandTip('view.piano', { hint: 'Shows the notes under the cursor and during playback on a piano keyboard.', side: 'top' })} aria-pressed={view.showPiano}>
          <Icon icon={{ svg: 'M3 6h18v12H3zM8 6v7M13 6v7M18 6v7M6 13h4M11 13h4M16 13h3' }} size={18} />
        </button>
        <button className={`tbtn icon-only${settingsOpen ? ' on' : ''}`} onClick={() => setState((s) => ({ settingsOpen: !s.settingsOpen, metronomeOpen: false }))} aria-label="Settings" {...tip('Settings', { side: 'top' })} aria-expanded={settingsOpen}>
          <Icon icon={{ svg: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z' }} size={18} />
        </button>
      </div>
    </div>
  );
}

function MetronomePopover() {
  const prefs = useStore((s) => s.playbackPrefs);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node) && !(e.target as HTMLElement).closest('.popwrap')) setState({ metronomeOpen: false });
    };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, []);
  const m = prefs.metronome;
  const c = prefs.countIn;
  return (
    <div className="popover metronome-pop" ref={ref} role="dialog" aria-label="Metronome and count-in">
      <Toggle label="Metronome" checked={m.enabled} onChange={(v) => setPlaybackPrefs({ metronome: { ...m, enabled: v } })} />
      <div className="pop-row">
        <span>Click</span>
        <Segmented label="Click frequency" value={m.frequency} options={[{ value: 'beat', label: 'Every beat' }, { value: 'bar', label: 'Every bar' }]} onChange={(v) => setPlaybackPrefs({ metronome: { ...m, frequency: v } })} />
      </div>
      <label className="pop-row">
        <span>Volume</span>
        <input type="range" min={0} max={1} step={0.05} value={m.volume} onChange={(e) => setPlaybackPrefs({ metronome: { ...m, volume: Number(e.target.value) } })} />
      </label>
      <hr />
      <Toggle label="Count-in" checked={c.enabled} onChange={(v) => setPlaybackPrefs({ countIn: { ...c, enabled: v } })} />
      <label className="pop-row">
        <span>Length</span>
        <select value={c.bars} onChange={(e) => setPlaybackPrefs({ countIn: { ...c, bars: Number(e.target.value) } })}>
          <option value={1}>1 bar</option>
          <option value={2}>2 bars</option>
        </select>
      </label>
    </div>
  );
}

const LAYOUTS: { value: ViewPrefs['layout']; label: string; hint: string }[] = [
  { value: 'scroll', label: 'Scroll', hint: 'Systems fill the window width and the score scrolls down.' },
  { value: 'paged', label: 'Paged', hint: 'Shows the score as separate pages on a grey desk.' },
  { value: 'horizontal', label: 'Horizontal', hint: 'All bars on one long line that scrolls sideways.' },
];

export function StatusBar() {
  const announce = useStore((s) => s.announce);
  const voice = useStore((s) => s.ed.cursor.voice);
  const lane = useStore((s) => s.ed.cursor.lane);
  const touch = useStore((s) => s.view.touchInput);
  const dur = useStore((s) => s.ed.duration);
  const zoom = useStore((s) => s.view.zoom);
  const layout = useStore((s) => s.view.layout);
  const valueKeys = `${formatShortcut('Alt+1')}–7`;
  const [hint, setHint] = useState(true);
  return (
    <div className="statusbar">
      <button className="sbtn" onClick={() => openModal('prefs')}>
        Editor preferences
      </button>
      <button className="sbtn" onClick={() => openModal('shortcuts')}>
        Keyboard shortcuts
      </button>
      <span className="status-info">
        Voice {voice + 1} · {lane === 'tab' ? 'Tab' : 'Staff'} · input{' '}
        {dur.base === 1 ? 'whole' : dur.base === 2 ? 'half' : dur.base === 4 ? 'quarter' : dur.base === 8 ? 'eighth' : `1/${dur.base}`}
        {dur.dots ? ' dotted' : ''}
        {dur.tuplet ? ' triplet' : ''}
      </span>
      {hint && (
        <span className="status-hint">
          <span className="status-hint-text">
            {lane === 'tab'
              ? `Type fret numbers · ↑↓ strings · ${valueKeys} note value · → next beat · drag notes from the left`
              : `Type A–G for notes · 2–8 add intervals · ${valueKeys} note value · → next beat`}
          </span>
          <button className="sbtn" aria-label="Hide hint" onClick={() => setHint(false)}>
            ×
          </button>
        </span>
      )}
      <span className="spacer" />
      <div className="layout-ctl" role="radiogroup" aria-label="Page layout">
        {LAYOUTS.map((l) => (
          <button key={l.value} role="radio" aria-checked={layout === l.value} className={`sbtn${layout === l.value ? ' on' : ''}`} {...tip(`Layout: ${l.label}`, { hint: l.hint, side: 'top' })} onClick={() => setView({ layout: l.value })}>
            {l.label}
          </button>
        ))}
      </div>
      <div className="zoom-ctl" role="group" aria-label="Zoom">
        <button className="sbtn" aria-label="Zoom out" {...commandTip('view.zoomOut', { side: 'top' })} onClick={() => zoomStep(-1)} disabled={zoom <= ZOOM_MIN}>
          −
        </button>
        <button className="sbtn zoom-value" {...commandTip('view.zoomReset', { hint: `${IS_MAC ? '⌘' : 'Ctrl'} + wheel or pinch to zoom.`, side: 'top' })} onClick={resetZoom} data-testid="zoom">
          {Math.round(zoom * 100)}%
        </button>
        <button className="sbtn" aria-label="Zoom in" {...commandTip('view.zoomIn', { side: 'top' })} onClick={() => zoomStep(1)} disabled={zoom >= ZOOM_MAX}>
          +
        </button>
      </div>
      <button className={`sbtn${touch ? ' on' : ''}`} onClick={() => setView({ touchInput: !touch })} aria-pressed={touch}>
        Touch input
      </button>
      <div className="sr-only" aria-live="polite" role="status">
        {announce}
      </div>
    </div>
  );
}
